import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import SoftBox from "../SoftBox";
import CardHeader, { CARD_ROOT, CARD_BODY_PAD } from "../CardHeader";
import { money, compact, C, FONT } from "../SalesBonus/common";
import { programFromConfig, ladderState } from "../SalesBonus/moneyRules";
import { realReps, isAverageRep } from "../../helpers/teamAverage";
import { metaByIdFromDefs } from "../../helpers/scoreAggregate";
import { hasMetricPlan } from "../../helpers/metricPlan";
import { payoutForValue, defaultPayoutCurve } from "../CustomWidget/payoutCurve";
import { FETCH_BONUS_PROGRESS } from "../../api/group";
import { companyToday } from "../../helpers/companyTime";
import { periodWindowLabel } from "../../helpers/periodWindowLabel";
import {
  fetchWorkspaceScores,
  monthsForWindow,
  resolveWindow,
  sumScores,
} from "../../pages/CompanyMetrics/dashboards/useWorkspaceData";

/**
 * Projected Annual Compensation — Year 1 rollup (Head of Sales mock).
 *
 * Lines:
 *   Base salary          — typed in Setup
 *   Q1…Q(n-1) bonuses    — live from chosen Scorekeeper metric × payout curve
 *   Qn bonus on pace     — same for the current quarter
 *   Annual growth kicker — live from milestone ladder YTD, or typed
 *
 * Projected Year 1 total = sum of those four.
 */

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const Row = ({ label, hint, value, gold }) => (
  <div
    style={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 12,
      padding: "7px 0",
      borderBottom: "1px solid var(--tvd-hair, #EEF2F7)",
    }}
  >
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: C.ink }}>{label}</div>
      {hint ? <div style={{ fontSize: 11, color: C.faint, marginTop: 1 }}>{hint}</div> : null}
    </div>
    <div
      style={{
        fontSize: 14,
        fontWeight: 600,
        color: gold ? "#B54708" : C.ink,
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </div>
  </div>
);

Row.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
  value: PropTypes.node.isRequired,
  gold: PropTypes.bool,
};
Row.defaultProps = {
  hint: null,
  gold: false,
};

/** Team booked/sold revenue for a bonus-progress payload (period in `ytd`). */
const teamRevenue = (payload) => {
  if (!payload) return 0;
  if (payload.totals && Number.isFinite(Number(payload.totals.ytd))) {
    return num(payload.totals.ytd);
  }
  const totalRow = (payload.reps || []).find(
    (r) => isAverageRep(r) && /total/i.test(String(r.name || "")),
  );
  if (totalRow) return num(totalRow.ytd);
  return realReps(payload.reps || []).reduce((s, r) => s + num(r.ytd), 0);
};

/** YTD revenue for the growth-kicker ladder (prefer team total when present). */
const teamYtdForKicker = (data) => {
  if (!data) return 0;
  if (data.totals && Number.isFinite(Number(data.totals.ytd))) {
    return num(data.totals.ytd);
  }
  const totalRow = (data.reps || []).find(
    (r) => isAverageRep(r) && /total/i.test(String(r.name || "")),
  );
  if (totalRow) return num(totalRow.ytd);
  const avg = (data.reps || []).find(isAverageRep);
  if (avg) return num(avg.ytd);
  return realReps(data.reps || []).reduce((s, r) => s + num(r.ytd), 0);
};

/** Pacific-company bounds for calendar quarter `q` (1–4) of `year`. */
const quarterRange = (year, q) => {
  const startMonth = (q - 1) * 3; // 0-indexed
  const from = companyToday().clone().year(year).month(startMonth).startOf("month").format("YYYY-MM-DD");
  const to = companyToday().clone().year(year).month(startMonth + 2).endOf("month").format("YYYY-MM-DD");
  return { from, to };
};

const nameKey = (s) => (s || "").trim().toLowerCase();

/** Resolve a catalog metric pick to an amount inside one workspace's perKpi map. */
const amountForMetric = (perKpi, defs, { metricId, metricLabel, sourceIds }) => {
  const candidates = [
    ...(Array.isArray(sourceIds) ? sourceIds : []),
    metricId,
  ].filter(Boolean).map(String);
  for (let i = 0; i < candidates.length; i += 1) {
    const cell = perKpi[candidates[i]];
    if (cell && Number.isFinite(Number(cell.amt))) return num(cell.amt);
  }
  const want = nameKey(metricLabel) || nameKey(metricId);
  if (!want) return 0;
  const def = (defs || []).find((d) => nameKey(d.title || d.name) === want);
  if (def && perKpi[String(def._id)]) return num(perKpi[String(def._id)].amt);
  return 0;
};


const poolWsAmounts = (amts, mode) => {
  const list = (amts || []).map(num);
  if (mode === "sum") return list.reduce((s, n) => s + n, 0);
  const live = list.filter((n) => n > 0);
  if (!live.length) return 0;
  return live.reduce((s, n) => s + n, 0) / live.length;
};

const fetchQuarterFromPlan = async (
  companyId,
  companyKpis,
  workspaceIds,
  year,
  q,
  columns,
) => {
  if (!companyId || !columns.length) return 0;
  const range = quarterRange(year, q);
  const months = monthsForWindow("custom", range);
  const { from, to } = resolveWindow("custom", range);

  const perWs = await Promise.all(
    workspaceIds.map(async (wsId) => {
      const defs = (companyKpis && companyKpis[wsId]) || [];
      const scores = await fetchWorkspaceScores(companyId, wsId, months);
      const { perKpi } = sumScores(scores, from, metaByIdFromDefs(defs), to);
      return { wsId, defs, perKpi };
    }),
  );

  return columns.reduce((sum, col) => {
    const amts = perWs.map(({ wsId, defs, perKpi }) => {
      const kpiId = col.sources && col.sources[wsId];
      if (kpiId && perKpi[String(kpiId)]) return num(perKpi[String(kpiId)].amt);
      return amountForMetric(perKpi, defs, {
        metricId: kpiId || col.key,
        metricLabel: col.label,
        sourceIds: Object.values(col.sources || {}),
      });
    });
    return sum + poolWsAmounts(amts, col.mode === "sum" ? "sum" : "avg");
  }, 0);
};

const fetchQuarterFromKpi = async (
  companyId,
  companyKpis,
  workspaceIds,
  year,
  q,
  metric,
) => {
  if (!companyId || !metric) return 0;
  const range = quarterRange(year, q);
  const months = monthsForWindow("custom", range);
  const { from, to } = resolveWindow("custom", range);
  const parts = await Promise.all(
    workspaceIds.map(async (wsId) => {
      const defs = (companyKpis && companyKpis[wsId]) || [];
      const scores = await fetchWorkspaceScores(companyId, wsId, months);
      const { perKpi } = sumScores(scores, from, metaByIdFromDefs(defs), to);
      return amountForMetric(perKpi, defs, metric);
    }),
  );
  return parts.reduce((s, n) => s + n, 0);
};

const fetchQuarterFromBonus = async (workspaceIds, year, q) => {
  const range = quarterRange(year, q);
  const pairs = await Promise.all(
    workspaceIds.map((id) =>
      FETCH_BONUS_PROGRESS(id, range)
        .then((res) => res)
        .catch(() => null),
    ),
  );
  return pairs.reduce((sum, payload) => sum + teamRevenue(payload), 0);
};

const ProjectedAnnualComp = ({
  data = null,
  title,
  primary = "#1B598E",
  settings = {},
  workspaceIds = [],
  period = null,
  customRange = null,
}) => {
  const companyId = useSelector((s) => s.authReducer.userDetails?.companyId);
  const companyKpis = useSelector((s) => s.kpiReducer.companyKpis);

  const today = companyToday();
  const year = today.year();
  const q = today.quarter();

  const baseSalary = num(settings.baseSalary, 100000);
  const priorManual = num(settings.priorBonuses, q <= 1 ? 0 : 40000);
  const currentManual = num(settings.q4Pace, 15625);
  const kickerManual = num(settings.kickerEarned, 5000);
  /* Target defaults to $1M so live math never stalls on an empty field. */
  const goalTarget = num(settings.goalTarget, 1000000) || 1000000;
  /* Curve is optional in Setup — if they picked metrics, use the stock tiers
     rather than falling back to typed zeros. */
  const curve = (settings.payoutCurve && settings.payoutCurve.on && (settings.payoutCurve.tiers || []).length)
    ? settings.payoutCurve
    : defaultPayoutCurve();
  const tiers = curve.tiers || [];

  const planCols = hasMetricPlan(settings.metricPlan)
    ? (settings.metricPlan.cols || []).filter((c) => c && c.show !== false)
    : [];
  const moneyCols = planCols.filter((c) => c.kind === "$");
  const selectable = moneyCols.length ? moneyCols : planCols;
  const storedKeys = Array.isArray(settings.revenueColumnKeys)
    ? settings.revenueColumnKeys.map(String)
    : [];
  const activeCols = (storedKeys.length
    ? selectable.filter((c) => storedKeys.includes(String(c.key)))
    : selectable);

  const metricId = settings.revenueMetricId || null;
  const metricLabel = settings.revenueMetricLabel || "";
  const sourceIds = Array.isArray(settings.revenueMetricIds) ? settings.revenueMetricIds : [];
  const hasPlan = activeCols.length > 0;
  const hasLegacy = !!(metricId || metricLabel);
  const metricPick = !hasPlan && hasLegacy
    ? { metricId, metricLabel, sourceIds }
    : null;

  /* Live needs metrics only. Curve + target auto-default above — no typed
     fallback required when live is on. */
  const liveReady = hasPlan || hasLegacy;
  /* Both prior + current default to live once metrics exist (opt-out). */
  const useLivePrior = settings.useLivePrior !== false && liveReady;
  const useLiveCurrent = settings.useLiveCurrent !== false && liveReady;
  const useLiveKicker = settings.useLiveKicker !== false;

  const wsKey = (workspaceIds || []).map(String).filter(Boolean).sort().join("|");
  const planKey = activeCols.map((c) => `${c.key}:${c.mode}:${JSON.stringify(c.sources || {})}`).join("|");
  const metricKey = `${metricId || ""}|${metricLabel}|${sourceIds.join(",")}|${planKey}`;

  const [livePrior, setLivePrior] = useState(null);
  const [liveCurrent, setLiveCurrent] = useState(null);
  const [liveBusy, setLiveBusy] = useState(false);

  useEffect(() => {
    const ids = wsKey ? wsKey.split("|") : [];
    if (!ids.length || (!useLivePrior && !useLiveCurrent)) {
      setLivePrior(null);
      setLiveCurrent(null);
      return undefined;
    }
    let alive = true;
    setLiveBusy(true);
    (async () => {
      try {
        const loadRev = (qq) => {
          if (hasPlan && companyId) {
            return fetchQuarterFromPlan(companyId, companyKpis, ids, year, qq, activeCols);
          }
          if (metricPick && companyId) {
            return fetchQuarterFromKpi(companyId, companyKpis, ids, year, qq, metricPick);
          }
          return fetchQuarterFromBonus(ids, year, qq);
        };

        let priorSum = 0;
        if (useLivePrior && q > 1) {
          const revenues = await Promise.all(
            Array.from({ length: q - 1 }, (_, i) => loadRev(i + 1)),
          );
          priorSum = revenues.reduce(
            (s, rev) => s + Math.round(payoutForValue(rev, goalTarget, tiers)),
            0,
          );
        }
        let currentPay = null;
        if (useLiveCurrent) {
          const rev = await loadRev(q);
          currentPay = Math.round(payoutForValue(rev, goalTarget, tiers));
        }
        if (!alive) return;
        setLivePrior(useLivePrior ? priorSum : null);
        setLiveCurrent(currentPay);
      } catch (e) {
        if (!alive) return;
        setLivePrior(null);
        setLiveCurrent(null);
      } finally {
        if (alive) setLiveBusy(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsKey, year, q, useLivePrior, useLiveCurrent, goalTarget, metricKey, companyId, JSON.stringify(tiers)]);

  const program = useMemo(
    () => programFromConfig(data && data.config),
    [data],
  );
  const ytd = useMemo(() => teamYtdForKicker(data), [data]);
  const ladder = useMemo(() => ladderState(program, ytd), [program, ytd]);

  const liveKicker = useLiveKicker && ladder.banked > 0 ? ladder.banked : null;
  let kickerEarned = kickerManual;
  if (useLiveKicker) kickerEarned = liveKicker != null ? liveKicker : (ladder.banked || 0);

  /* When live is on, never paint typed fallbacks — show 0 until fetch lands. */
  let priorBonuses = priorManual;
  if (useLivePrior) priorBonuses = livePrior != null ? livePrior : 0;
  let currentBonus = currentManual;
  if (useLiveCurrent) currentBonus = liveCurrent != null ? liveCurrent : 0;

  const projected = baseSalary + priorBonuses + currentBonus + kickerEarned;

  const priorLabel = q <= 1 ? "Prior-quarter performance bonuses" : `Q1–Q${q - 1} performance bonuses`;
  const currentLabel = `Q${q} performance bonus`;
  let metricHint = metricLabel || "chosen metric";
  if (hasPlan) {
    metricHint = activeCols.map((c) => c.label || c.key).filter(Boolean).join(" + ") || "metric plan";
  }

  let priorHint = "typed in Setup";
  if (useLivePrior && livePrior != null) {
    priorHint = liveBusy
      ? "live · updating…"
      : `live · ${metricHint} × payout curve`;
  } else if (settings.useLivePrior !== false && !liveReady) {
    priorHint = "pick a metric column under Setup";
  } else if (useLivePrior) priorHint = "live · loading…";

  let currentHint = "typed in Setup";
  if (useLiveCurrent && liveCurrent != null) {
    currentHint = liveBusy
      ? "live · updating…"
      : `live · ${metricHint} × payout curve`;
  } else if (settings.useLiveCurrent !== false && !liveReady) {
    currentHint = "pick a metric column under Setup";
  } else if (useLiveCurrent) currentHint = "live · loading…";

  /* The kicker ladder climbs whatever window the board is on — say which one
     instead of always printing "YTD" under a Last-week / quarter figure. */
  const windowLabel = periodWindowLabel(period || "ytd", customRange);
  let kickerHint = "typed in Setup";
  if (liveKicker != null && ytd > 0) kickerHint = `live · ${compact(ytd)} ${windowLabel} across scoped teams`;
  else if (ytd > 0) kickerHint = `at ${compact(ytd)} ${windowLabel}`;

  const heading = (title && title.trim()) || "Projected annual compensation";
  /* ladderState().next is the rung object itself, not a list — indexing [0]
     made this null forever and the "unlock the next tier" banner never drew. */
  const nextRung = ladder.next || null;
  const toNext = nextRung ? Math.max(0, num(nextRung.target) - ytd) : 0;

  return (
    <SoftBox sx={{ ...CARD_ROOT, fontFamily: FONT }}>
      <div data-hug-content style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", minHeight: 0 }}>
        <CardHeader title={heading} />
        <SoftBox sx={{ padding: CARD_BODY_PAD, paddingTop: 8, paddingBottom: 14 }}>
          {toNext > 0 && nextRung && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                background: "var(--tvd-tile, #F0F7FF)",
                border: "1px solid var(--tvd-hair, #D6E8FF)",
                borderRadius: 8,
                padding: "8px 10px",
                marginBottom: 12,
              }}
            >
              <span style={{ fontSize: 12, color: "var(--tvd-body, #344054)", lineHeight: 1.35 }}>
                {`${compact(toNext)} more revenue ${windowLabel} to unlock the next kicker tier.`}
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#B54708", whiteSpace: "nowrap" }}>
                +{money(num(nextRung.bonus))}
              </span>
            </div>
          )}

          <div style={{ marginBottom: 4, fontSize: 11, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: primary }}>
            Projected annual compensation
          </div>
          <div style={{ fontSize: 30, fontWeight: 600, color: C.ink, letterSpacing: "-0.03em", lineHeight: 1.1 }}>
            {money(projected)}
          </div>
          <div style={{ fontSize: 12, color: C.faint, marginTop: 4, lineHeight: 1.4 }}>
            Base + {q > 1 ? `Q1–Q${q - 1}` : "prior"} + {`Q${q}`} on pace + growth kicker
          </div>

          <div style={{ marginTop: 10 }}>
            <Row label="Base salary" hint="typed in Setup" value={money(baseSalary)} />
            {q > 1 ? (
              <Row label={priorLabel} hint={priorHint} value={money(priorBonuses)} />
            ) : null}
            <Row label={currentLabel} hint={currentHint} value={money(currentBonus)} gold />
            <Row label="Annual growth kicker" hint={kickerHint} value={money(kickerEarned)} gold />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              gap: 12,
              marginTop: 10,
              paddingTop: 10,
              borderTop: "2px solid var(--tvd-hair, #E4E7EC)",
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>Projected Year 1 total</span>
            <span style={{ fontSize: 17, fontWeight: 600, color: "#B54708", fontVariantNumeric: "tabular-nums" }}>
              {money(projected)}
            </span>
          </div>
        </SoftBox>
      </div>
    </SoftBox>
  );
};

ProjectedAnnualComp.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object,
  title: PropTypes.string,
  primary: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  settings: PropTypes.object,
  workspaceIds: PropTypes.arrayOf(PropTypes.string),
  period: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  customRange: PropTypes.object,
};
ProjectedAnnualComp.defaultProps = {
  data: null,
  title: undefined,
  primary: "#1B598E",
  settings: {},
  workspaceIds: [],
  period: null,
  customRange: null,
};

export default ProjectedAnnualComp;
